import { storageGet, storageRemove, storageSet } from "./storage";
import { normalizeZillowUrl } from "./zillowUrl";

const STORAGE_KEY = "liq_pending_zillow_import";
// How long a recovery record can still describe an import that is really in
// flight. An import takes minutes; past this it is abandoned, not resumable.
// Without an expiry the record outlived everything: it survived sign-out, and
// every later visit either resumed the old listing or refused a new property
// until the customer cleared their site data (Trello gWHnPJB0).
const SESSION_TTL_MS = 30 * 60 * 1000;
const REQUEST_ID = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const CAPABILITY = /^[A-Za-z0-9_-]{32,128}$/;

/**
 * @typedef {{schema: 1, requestId: string, sourceUrl: string, funnelToken: string,
 * listingUuid: string | null, startedAt: number, persisted: boolean, manualRetryRequired?: boolean}} ImportSession
 */
/** @type {ImportSession | null} */
let memorySession = null;
/** A failed remove must not resurrect an acknowledged import in this tab. */
let clearedRequestId = "";

/** @param {unknown} value @returns {value is ImportSession} */
const validSession = (value) => {
  const record = /** @type {Partial<ImportSession> | null} */ (value);
  return (
    !!record &&
    record.schema === 1 &&
    typeof record.requestId === "string" &&
    REQUEST_ID.test(record.requestId) &&
    typeof record.sourceUrl === "string" &&
    normalizeZillowUrl(record.sourceUrl) === record.sourceUrl &&
    typeof record.funnelToken === "string" &&
    CAPABILITY.test(record.funnelToken) &&
    (record.listingUuid === null || typeof record.listingUuid === "string") &&
    (record.manualRetryRequired === undefined || typeof record.manualRetryRequired === "boolean") &&
    typeof record.startedAt === "number" &&
    Number.isFinite(record.startedAt)
  );
};

/** @returns {ImportSession | null} */
export const loadZillowImportSession = () => {
  if (memorySession) return memorySession;
  try {
    const raw = storageGet(STORAGE_KEY);
    const record = raw ? JSON.parse(raw) : null;
    if (validSession(record) && record.requestId !== clearedRequestId) {
      if (Date.now() - record.startedAt > SESSION_TTL_MS) {
        // Drop it rather than hand back something stale: a caller that sees a
        // record treats it as an import worth resuming.
        storageRemove(STORAGE_KEY);
        memorySession = null;
        return null;
      }
      return { ...record, persisted: true };
    }
  } catch {
    // A corrupt/blocked recovery store must not crash the entry page.
  }
  return memorySession;
};

/** @param {ImportSession} record @returns {ImportSession} */
const saveSession = (record) => {
  const serialized = JSON.stringify({ ...record, persisted: true });
  storageSet(STORAGE_KEY, serialized);
  const saved = { ...record, persisted: storageGet(STORAGE_KEY) === serialized };
  memorySession = saved.persisted ? null : saved;
  clearedRequestId = "";
  return saved;
};

/** @param {number} size */
const randomBytes = (size) => {
  if (!window.crypto?.getRandomValues) {
    throw new Error(
      "This browser cannot securely start an import. Use a current browser over HTTPS to import a listing."
    );
  }
  return window.crypto.getRandomValues(new Uint8Array(size));
};

/** @param {Uint8Array} bytes */
const hex = (bytes) => Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

/** Allocate and save BOTH replay identity and anonymous capability before POST. */
/** @param {string} source @returns {ImportSession} */
export const prepareZillowImportSession = (source) => {
  const sourceUrl = normalizeZillowUrl(source);
  if (!sourceUrl) throw new Error("Enter a valid Zillow property listing link.");
  const previous = loadZillowImportSession();
  if (previous) {
    if (previous.sourceUrl !== sourceUrl) {
      throw new Error("Resume or stop your current import before starting another property.");
    }
    return previous;
  }
  const bytes = randomBytes(16);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const id = hex(bytes);
  return saveSession({
    schema: 1,
    requestId: `${id.slice(0, 8)}-${id.slice(8, 12)}-${id.slice(12, 16)}-${id.slice(16, 20)}-${id.slice(20)}`,
    sourceUrl,
    funnelToken: hex(randomBytes(32)),
    listingUuid: null,
    startedAt: Date.now(),
    persisted: false,
  });
};

/** Associate a response only with the still-current request; never replace a newer recovery record. */
/** @param {string} requestId @param {string} listingUuid */
export const bindZillowImportListing = (requestId, listingUuid) => {
  const record = loadZillowImportSession();
  if (!record || record.requestId !== requestId || !listingUuid) return null;
  if (record.listingUuid && record.listingUuid !== listingUuid) return null;
  return saveSession({ ...record, listingUuid });
};

/** A known rejected POST must not become an automatic replay after a later reload.
 * Keep the same identity and never modify a different request's recovery record.
 * @param {string} requestId
 */
export const requireManualZillowImportRetry = (requestId) => {
  const record = loadZillowImportSession();
  if (!record || record.requestId !== requestId) return null;
  return saveSession({ ...record, manualRetryRequired: true });
};

/** Compare-and-clear; a late completion may not delete a newer import's recovery data. */
/** @param {string} requestId */
export const clearZillowImportSession = (requestId) => {
  const current = loadZillowImportSession();
  if (!current || current.requestId !== requestId) return false;
  storageRemove(STORAGE_KEY);
  clearedRequestId = requestId;
  memorySession = null;
  return true;
};

/** @param {any} listing */
export const isListingImportActive = (listing) =>
  ["queued", "processing"].includes(listing?.import?.status);

export const IMPORT_STATUSES = ["queued", "processing", "ready", "partial", "failed", "cancelled"];
